export const masterFormula = {
  title: 'Total Trade Cost',
  formula: 'Spread Cost + Commission + Swap',
  description: 'Every forex trade pays three costs. Spread is paid on entry. Commission is paid on entry and exit. Swap accrues every night you hold. Add them together — that is the real price of your trade.',
} as const;

export const spreadStep = {
  stepNumber: 1,
  title: 'Calculate Spread Cost',
  tagline: 'The entry toll — paid the microsecond you click',
  formula: 'Spread Cost = Spread (pips) × Lot Size (lots) × Pip Value ($/pip)',
  breakdown: [
    { label: 'Spread (pips)', symbol: 'spreadPips', desc: 'The gap between bid and ask. EURUSD averages 0.8–1.2 pips on standard accounts, 0.1–0.3 on ECN.' },
    { label: 'Lot Size (lots)', symbol: 'lotSize', desc: '1.0 = standard lot (100k units), 0.1 = mini, 0.01 = micro. Most retail traders use 0.01–1.0.' },
    { label: 'Pip Value ($/pip)', symbol: 'pipValue', desc: 'EURUSD = $10/standard lot. USDJPY ≈ $9. GBPUSD = $10. Cross pairs vary — check your broker.' },
  ],
  examples: [
    { pair: 'EURUSD', spreadPips: 1.2, lotSize: 1.0, pipValue: 10.0, result: 12.00, note: 'Standard account, London session' },
    { pair: 'EURUSD', spreadPips: 0.2, lotSize: 1.0, pipValue: 10.0, result: 2.00, note: 'ECN account, London session' },
    { pair: 'USDJPY', spreadPips: 1.0, lotSize: 0.5, pipValue: 9.0, result: 4.50, note: 'Mini lot, Tokyo session' },
    { pair: 'GBPUSD', spreadPips: 1.5, lotSize: 0.1, pipValue: 1.0, result: 0.15, note: 'Micro lot, NY session' },
  ],
  insight: 'Spread is the only cost that is non-negotiable — every single trade pays it, regardless of account type, broker, or holding period. Scalpers feel it the hardest: a 1.2 pip spread costs $12/lot, and 15 round-turns a day means $180/day just in spread.',
} as const;

export const commissionStep = {
  stepNumber: 2,
  title: 'Calculate Commission',
  tagline: 'The broker\'s cut — transparent on ECN, hidden in spread on standard',
  formula: 'Commission = Commission per Side ($) × 2 (round-trip) × Lot Size (lots)',
  breakdown: [
    { label: 'Commission per Side', symbol: 'commissionPerSide', desc: 'ECN brokers charge per side (entry + exit = 2×). Typical rate: $3.50–$7.00/side for majors.' },
    { label: 'Round-Trip Multiplier', symbol: 'isRoundTrip', desc: '× 2 for per-side pricing. Some brokers charge "per round-turn" — in that case, use × 1.' },
    { label: 'Lot Size (lots)', symbol: 'lotSize', desc: 'Same as spread calculation. Commission scales linearly with position size.' },
  ],
  examples: [
    { label: 'ECN — EURUSD 1 lot', commissionPerSide: 3.50, isPerSide: true, lotSize: 1.0, result: 7.00, note: '$3.50 × 2 sides × 1.0 lot' },
    { label: 'ECN — EURUSD 0.1 lot', commissionPerSide: 3.50, isPerSide: true, lotSize: 0.1, result: 0.70, note: '$3.50 × 2 × 0.1 = $0.70' },
    { label: 'Round-Turn — GBPJPY 1 lot', commissionPerSide: 9.00, isPerSide: false, lotSize: 1.0, result: 9.00, note: '$9.00 RT × 1.0 lot (no ×2)' },
    { label: 'Standard Account', commissionPerSide: 0, isPerSide: true, lotSize: 1.0, result: 0, note: 'Commission-free — cost is baked into wider spread' },
  ],
  insight: 'Commission is the silent killer for scalpers. At 20 round-turns/day with 0.1 lots at $3.50/side, that is $14/day — $280/month — before the market moves a single pip. But for a swing trader doing 4 round-turns/month at 2.0 lots, commission is just $56 — barely noticeable.',
} as const;

export const swapStep = {
  stepNumber: 3,
  title: 'Calculate Swap / Rollover',
  tagline: 'The overnight rent — zero for day traders, massive for position traders',
  formula: 'Swap Cost = Swap Rate ($/lot/day) × Lot Size (lots) × Days Held',
  breakdown: [
    { label: 'Swap Rate ($/lot/day)', symbol: 'swapRate', desc: 'Long EURUSD ≈ -$4.50/day. Short EURUSD ≈ +$1.20/day. Swap can be positive or negative depending on interest rate differential.' },
    { label: 'Lot Size (lots)', symbol: 'lotSize', desc: 'Same as above. Swap scales linearly — 0.1 lots pays 1/10th the swap rate.' },
    { label: 'Days Held', symbol: 'daysHeld', desc: 'Count calendar days. Wednesday rollover charges 3× swap (covers weekend). Some brokers charge swap on Saturday too.' },
  ],
  tripleSwapNote: 'Wednesday rollover = 3× daily swap. If you hold through Wednesday 5 PM ET, you pay 3 days of swap at once to cover the weekend. Holding Tuesday→Thursday = 1 + 3 + 1 = 5 days of swap for 3 calendar days.',
  examples: [
    { pair: 'EURUSD Long', swapRate: -4.50, lotSize: 1.0, daysHeld: 1, result: -4.50, hasTriple: false, note: '1 day hold, no Wednesday' },
    { pair: 'EURUSD Long', swapRate: -4.50, lotSize: 1.0, daysHeld: 7, result: -31.50, hasTriple: false, note: '1 week hold' },
    { pair: 'EURUSD Long', swapRate: -4.50, lotSize: 1.0, daysHeld: 30, result: -135.00, hasTriple: false, note: '1 month hold' },
    { pair: 'EURUSD Short', swapRate: 1.20, lotSize: 1.0, daysHeld: 7, result: 8.40, hasTriple: false, note: 'Positive swap — you get paid to hold short' },
    { pair: 'USDTRY Long', swapRate: -35.00, lotSize: 0.1, daysHeld: 14, result: -49.00, hasTriple: false, note: 'Exotic pairs have massive swap rates' },
    { pair: 'EURUSD Long (Wed)', swapRate: -4.50, lotSize: 1.0, daysHeld: 3, result: -22.50, hasTriple: true, note: 'Wed→Fri: 1 + 3 (Wed×) + 1 = 5 days swap' },
  ],
  insight: 'Swap is invisible on the trade ticket but dominates for any position held more than 2 days. A EURUSD long held for 30 days at -$4.50/day costs $135 — far more than the $12 spread. Position traders should care more about swap rates than spread width.',
  costDominanceNote: 'If your position is held ≥ 5 days, swap cost usually exceeds spread + commission combined. The longer you hold, the less your entry costs matter — and the more swap dominates your P&L.',
} as const;

export const totalFormulaExamples = [
  {
    persona: 'Scalper — Marco',
    pair: 'EURUSD',
    lotSize: 0.1,
    accountType: 'ECN',
    spreadPips: 0.2,
    pipValuePerLot: 10,
    commissionPerSide: 3.50,
    isPerSide: true,
    swapRate: -0.45,
    daysHeld: 0,
    spreadCost: 0.20,
    commissionCost: 0.70,
    swapCost: 0,
    totalCost: 0.90,
    totalFormulaStr: '$0.20 + $0.70 + $0.00',
    insight: 'Scalpers pay almost entirely in spread + commission. Swap is zero because positions never survive rollover. For Marco doing 20 round-turns/day, this adds up to $18/day.',
    dominantCost: 'Commission',
    dominantPct: 78,
  },
  {
    persona: 'Day Trader — Sarah',
    pair: 'GBPUSD',
    lotSize: 1.0,
    accountType: 'Standard',
    spreadPips: 1.5,
    pipValuePerLot: 10,
    commissionPerSide: 0,
    isPerSide: true,
    swapRate: -6.50,
    daysHeld: 1,
    spreadCost: 15.00,
    commissionCost: 0,
    swapCost: 6.50,
    totalCost: 21.50,
    totalFormulaStr: '$15.00 + $0.00 + $6.50',
    insight: 'Day traders on standard accounts pay mostly spread. One overnight hold adds swap — but spread still dominates. Sarah trades 5×/day = $107.50/day in costs.',
    dominantCost: 'Spread',
    dominantPct: 70,
  },
  {
    persona: 'Swing Trader — Kenji',
    pair: 'USDJPY',
    lotSize: 2.0,
    accountType: 'ECN',
    spreadPips: 0.3,
    pipValuePerLot: 9,
    commissionPerSide: 5.00,
    isPerSide: true,
    swapRate: -7.20,
    daysHeld: 7,
    spreadCost: 5.40,
    commissionCost: 20.00,
    swapCost: 100.80,
    totalCost: 126.20,
    totalFormulaStr: '$5.40 + $20.00 + $100.80',
    insight: 'Swap completely dominates for swing traders. After 7 days, the overnight cost is 5× larger than spread + commission combined. Entry cost is almost rounding error.',
    dominantCost: 'Swap',
    dominantPct: 80,
  },
  {
    persona: 'Position Trader — Anna',
    pair: 'EURUSD',
    lotSize: 5.0,
    accountType: 'ECN',
    spreadPips: 0.2,
    pipValuePerLot: 10,
    commissionPerSide: 3.50,
    isPerSide: true,
    swapRate: -4.50,
    daysHeld: 30,
    spreadCost: 10.00,
    commissionCost: 35.00,
    swapCost: 675.00,
    totalCost: 720.00,
    totalFormulaStr: '$10.00 + $35.00 + $675.00',
    insight: 'For a 30-day position at 5 lots, swap is $675 — while spread + commission is just $45. Anna should negotiate swap rates with her broker, not spread width.',
    dominantCost: 'Swap',
    dominantPct: 94,
  },
] as const;

export const calculateFAQs = [
  {
    question: 'What is the complete formula for total forex trading cost?',
    answer: 'Total Cost = (Spread in pips × Lot Size × Pip Value) + (Commission per Side × 2 × Lot Size) + (Swap Rate per Lot × Lot Size × Days Held). For per-side commissions, multiply by 2 for the round-trip. For round-turn commissions, do not double. For swap, Wednesday rollover charges 3× the daily rate to cover the weekend. Add these three numbers and you have the exact dollar cost of your trade before the market moves.',
    category: 'The Master Formula',
  },
  {
    question: 'Do I always pay all three costs on every trade?',
    answer: 'Nearly always. Spread is universal — every trade pays it, no exceptions. Commission depends on your account type: market maker/standard accounts have $0 explicit commission (it is baked into wider spreads), ECN accounts charge transparent commission. Swap is the only truly avoidable cost: if you close your position before 5 PM ET (rollover time), you pay $0 swap. Day traders and scalpers rarely pay swap. Swing and position traders always do.',
    category: 'The Master Formula',
  },
  {
    question: 'How do I calculate pip value for any currency pair?',
    answer: 'For pairs where USD is the quote currency (EURUSD, GBPUSD, AUDUSD): 1 pip on 1 standard lot = $10. For pairs where USD is the base (USDJPY, USDCAD): 1 pip on 1 standard lot ≈ $10 × (exchange rate denominator ÷ current price). Simplified: USDJPY ≈ $9/standard lot at 110, USDCAD ≈ $8 at 1.25. For crosses (EURGBP, GBPJPY): calculate the quote currency pip value then convert to USD at current market rate. Most platforms display pip value in the order window — use that number.',
    category: 'The Master Formula',
  },
  {
    question: 'Do swap rates change daily?',
    answer: 'Yes. Swap rates are based on the interest rate differential between the two currencies in the pair. When the Federal Reserve or ECB changes rates, swap rates adjust within 1–2 business days. Brokers also add their own markup on top of the interbank swap rate. Two brokers can show different swap rates for the same pair. Always check current swap rates before holding positions overnight — they can change during central bank meetings.',
    category: 'Swap Details',
  },
  {
    question: 'What exactly is triple swap on Wednesday?',
    answer: 'Forex settles T+2 (trade date + 2 business days). A trade opened Wednesday settles on Friday. Holding through Wednesday 5 PM ET means the position value date rolls to Monday — spanning 3 calendar days (Sat, Sun, Mon). The broker charges 3× the daily swap to cover the weekend. Some brokers charge triple swap on Friday instead. Check your broker\'s specific rollover policy. If you open a trade Monday and close before Wednesday rollover, you pay only 2 days of swap — no triple.',
    category: 'Swap Details',
  },
  {
    question: 'Can swap be positive — meaning I get paid to hold?',
    answer: 'Yes. If you buy a currency with a higher interest rate and sell one with a lower rate, you earn positive swap. For example: long USDTRY (Turkish lira has high rates) earns positive swap, short USDTRY pays negative swap. However, positive swap pairs often have wider spreads and higher volatility — the "free money" is priced in. Do not chase positive swap blindly; the currency pair can move against you and wipe out months of swap earnings in one session.',
    category: 'Swap Details',
  },
  {
    question: 'How do I find my broker\'s exact swap rates?',
    answer: 'Most brokers publish swap rates on their website under "Contract Specifications" or "Trading Conditions." In MT4/MT5: right-click a pair in Market Watch → Specification → Swap Long / Swap Short. In cTrader: check the symbol specifications panel. Rates are shown in points (not dollars) — multiply by pip value to get the dollar amount. Example: Swap Long = -4.50 points on EURUSD means -$4.50/day per standard lot. Always check directly on the platform, not the marketing website.',
    category: 'Swap Details',
  },
  {
    question: 'Is ECN always cheaper than standard accounts when calculating total cost?',
    answer: 'Not always — it depends on volume and holding period. For high-volume scalpers: ECN almost always cheaper (lower spread + transparent commission beats baked-in wide spread). For low-volume swing traders holding 7+ days: the difference shrinks because swap dominates both account types equally. Run the numbers: calculate total cost = spread + commission + swap for both account types on your specific pair, lot size, and holding period. There is no universal answer.',
    category: 'Account Comparison',
  },
  {
    question: 'How does lot size affect the total cost formula?',
    answer: 'Everything scales linearly with lot size. Double the lot size → double the spread cost, double the commission, double the swap. A 0.1 lot trade costs exactly 10% of a 1.0 lot trade. This is why position sizing is the single most powerful cost control lever you have. Trading 0.5 lots instead of 1.0 cuts your total cost in half — without changing your strategy, pair, or broker. Many traders obsess over 0.1 pip spread differences while ignoring the 50% cost reduction from halving their lot size.',
    category: 'Cost Optimization',
  },
  {
    question: 'Why do exotic pairs cost so much more to trade?',
    answer: 'Three reasons compound: (1) wider spreads — USDTRY can have 20–100 pip spreads vs 0.8 for EURUSD, (2) higher commissions — exotic pairs often have $7–10/side commission vs $3.50 for majors, and (3) massive swap rates — USDTRY long swap can be -$35/day/lot vs -$4.50 for EURUSD. A 0.1 lot USDTRY trade held 7 days can cost $50+ while the same EURUSD trade costs $5. The lower liquidity, higher volatility, and extreme interest rate differentials make exotics expensive by design.',
    category: 'Pair Selection',
  },
  {
    question: 'How do I reduce my total trading costs?',
    answer: 'Four levers ranked by impact: (1) Reduce lot size — cuts all three costs proportionally, the biggest single lever. (2) Trade during liquid sessions (London/NY overlap) — reduces spread by 3–5× vs quiet hours. (3) Avoid holding through Wednesday rollover if possible — saves 3× swap. (4) Choose ECN account if doing 20+ lots/month — saves 25–40% on spread+commission. Swapping from daily to 4-hour charts and reducing trade frequency from 5/day to 2/day often cuts costs more than switching brokers.',
    category: 'Cost Optimization',
  },
  {
    question: 'Should I include slippage in the total cost formula?',
    answer: 'Yes, for a complete picture. Total Real Cost = Spread + Commission + Swap + Slippage. Slippage is the difference between the price you clicked and the price you got — it averages 0.1–0.5 pips on liquid pairs during normal conditions, and 2–10+ pips during news. For exact accounting: calculate spread+commission+swap using the formulas above, then add your observed average slippage from your last 20 trades. The hidden and slippage pages on this site cover these costs in depth.',
    category: 'Beyond the Formula',
  },
] as const;

export const relatedCalculateGuides = [
  {
    title: 'Forex Spread Explained',
    href: '/forex-spread/',
    desc: 'The bid-ask gap is the most visible cost — and the easiest to plan around. Know when spreads are widest so you can avoid them.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Forex Commission Explained',
    href: '/forex-commission/',
    desc: 'Commission-free sounds great, right? Not always. See when paying a flat fee per lot actually leaves more money in your pocket.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Forex Swap Explained',
    href: '/forex-swap/',
    desc: 'If you hold trades for weeks, swap is probably your biggest cost by far. Here\'s exactly how it\'s calculated each night.',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Costs by Strategy',
    href: '/forex-trading-costs-by-strategy/',
    desc: 'Scalper, day trader, swing trader, position trader — each style has a different cost villain. Find out which one is yours.',
    icon: 'ri-line-chart-line',
  },
] as const;